import React, { PureComponent } from 'react';
import {
  WithStyles,
  createStyles,
  withStyles,
  WithTheme,
  withTheme,
  Theme,
} from '@lifeomic/chroma-react/styles';
import SurveyItem from '../util/SurveyItem';
import clsx from 'clsx';
import { FIELD_WIDTHS, inputCarriesFieldWidth } from '../util/fieldWidths';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      maxWidth: '100%',
      display: 'flex',
    },
    cappedWidth: {
      // A sized item has to be allowed below its content's min-content width,
      // or controls that refuse to shrink (selects especially) keep their
      // natural size and overflow the cap, escaping the section or the
      // repeating-group instance card. Scoped to the cap so uncapped items
      // across other surveys keep their existing sizing.
      minWidth: 0,
      // chroma hard-codes a min-width on both the control and its container
      // (180px on TextField, 175px on TextArea), which is wider than xs and
      // matches sm. Without relaxing it the input keeps that width regardless
      // of the declared one and slides out from under it, colliding with the
      // unit suffix beside it. The container nests at different depths with and
      // without a unit suffix, so relax every descendant rather than guessing
      // the level. The doubled
      // selector is required: a single class ties with chroma's own class and
      // loses or wins purely on JSS injection order.
      '&& *': {
        minWidth: 0,
      },
    },
    margins: {
      margin: theme.spacing(3, 0),
    },
    denseMargins: {
      // Dense questions still need enough separation for each label to read
      // with the control below it rather than the preceding control.
      margin: theme.spacing(1, 0),
    },
    indented: {
      marginLeft: theme.spacing(8),
    },
    denseIndented: {
      marginLeft: theme.spacing(5),
    },
    // Opt-in (body-label modifierExtension): restyle the wrapped control's
    // label as chroma `body` (16px regular) so a standalone question reads at
    // the same weight as adjacent table-group rows. The container wraps a
    // single control, and the extension is only authored on string/text/
    // choice-dropdown items (never radio groups), so a descendant `label`
    // selector is safe. Chroma's own label class is JSS-hashed (no stable
    // hook), and ties on specificity: the doubled selector wins regardless of
    // JSS injection order.
    bodyLabel: {
      '&& label': {
        fontSize: theme.pxToRem(16),
        fontWeight: theme.typography.fontWeightRegular,
        lineHeight: theme.pxToRem(26),
      },
    },
    // body-label + bold-title on the same item: keep the body size but bold
    // the weight, so a standalone question's label reads as a header matching
    // bold table-group titles. Same doubled selector as bodyLabel (equal
    // specificity); declared after it in this sheet, so JSS's stable in-sheet
    // order guarantees the weight override wins.
    bodyLabelBold: {
      '&& label': {
        fontWeight: theme.typography.fontWeightBold,
      },
    },
    // Calculated fields stay disabled, but a grey disabled box reads as broken.
    // Accent border + light tint mark them as derived without looking like
    // focus or validation errors; text stays at full contrast.
    calculatedChrome: {
      '& input:disabled, & textarea:disabled': {
        border: `2px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.hexToRgba(theme.palette.primary.main, 0.08),
        color: theme.palette.text.primary,
        WebkitTextFillColor: theme.palette.text.primary,
        opacity: 1,
        cursor: 'default',
      },
    },
  });

export interface SurveyItemContainerOwnProps {
  children: any;
  surveyItem: SurveyItem;
}

export type SurveyItemContainerProps = SurveyItemContainerOwnProps &
  WithStyles<typeof styles> &
  WithTheme;

class SurveyItemContainer extends PureComponent<SurveyItemContainerProps> {
  render() {
    const { surveyItem, classes, theme } = this.props;

    const fieldWidth = surveyItem.fieldWidth;
    const declaredWidth = fieldWidth ? FIELD_WIDTHS[fieldWidth] : undefined;
    // Controls that carry the width themselves (quantities, whose unit sits
    // beside the input) must not also be sized here, or the declared width
    // would be shared between the input and the unit text.
    const containerWidth = inputCarriesFieldWidth(surveyItem.type)
      ? undefined
      : declaredWidth;
    // `width`, not `maxWidth`: the wrapper above sizes a field-width item from
    // its content, so a ceiling alone leaves the box as wide as its label.
    // `root`'s maxWidth: 100% plus the minWidth: 0 below still let it shrink
    // when the row is narrower than the declared width.
    const widthStyle = containerWidth ? { width: containerWidth } : undefined;

    // Table row details supply their own indent/well; extra container
    // margins would float the field away from its trigger row.
    const calculatedClass = surveyItem.hasCalculatedValue
      ? classes.calculatedChrome
      : undefined;
    // Applied on both paths: a row detail's own control label is still the
    // default chroma label (TableRowDetail only supplies the indent/well), so
    // the extension must work there too.
    const bodyLabelClass = surveyItem.hasBodyLabel
      ? clsx(
          classes.bodyLabel,
          surveyItem.hasBoldTitle && classes.bodyLabelBold,
        )
      : undefined;

    if (surveyItem.isTableRowDetail) {
      return (
        <div
          className={clsx(
            classes.root,
            declaredWidth && classes.cappedWidth,
            calculatedClass,
            bodyLabelClass,
          )}
          style={widthStyle}
          data-testid={
            surveyItem.hasCalculatedValue
              ? `survey-calculatedField-${surveyItem.link}`
              : undefined
          }
        >
          {this.props.children}
        </div>
      );
    }

    const isDense = surveyItem.hasDenseMargins;
    const isIndented = surveyItem.isIndented;

    const classNames = [
      classes.root,
      isDense ? classes.denseMargins : classes.margins,
      declaredWidth ? classes.cappedWidth : undefined,
      calculatedClass,
      bodyLabelClass,
    ].filter(Boolean);

    const indentationCount =
      (isDense ? 3 : 0) + (isIndented ? surveyItem.indentationCount : 0);

    const marginLeft = theme.spacing(indentationCount);

    return (
      <div
        className={clsx(...classNames)}
        style={{
          marginLeft,
          ...widthStyle,
        }}
        data-testid={
          surveyItem.hasCalculatedValue
            ? `survey-calculatedField-${surveyItem.link}`
            : undefined
        }
      >
        {this.props.children}
      </div>
    );
  }
}

export default withTheme(withStyles(styles)(SurveyItemContainer));
